import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Switch } from '@material-ui/core';
import { MdSearch } from 'react-icons/md';
import TextField from 'components/TextField';
import ProfilesActions from 'store/ducks/profiles';
import ProvidersActions from 'store/ducks/providers';
import { MODULES } from 'config/modules';
import {
  Container,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  HeaderButtons,
  HeaderSaveButton,
  HeaderCancelButton,
  StepsContainer,
  Step,
  StepNumber,
  FormContent,
  LeftPanel,
  RightPanel,
  Section,
  SectionTitle,
  WrapperInput,
  ToggleContainer,
  ToggleLabel,
  ChipsContainer,
  Chip,
  CompaniesSection,
  CompanyCheckbox,
  SearchButton,
  PermissionsSection,
  PermissionsHeader,
  PermissionsTitle,
  PermissionsTableHeader,
  PermissionsTableRow,
  PermissionsTableCell,
  PermissionsTableActions,
  SelectAllContainer,
  SearchIconContainer,
  ModalFooter,
  SaveButton,
  CancelButton,
} from './styles';

// Tipos de cadastros disponíveis
const REGISTRATION_TYPES = [
  { value: 'cargas', label: 'Cargas' },
  { value: 'viagens', label: 'Viagens' },
  { value: 'pedidos_frete', label: 'Pedido de Frete' },
  { value: 'embarcadores', label: 'Embarcadores' },
  { value: 'fretes', label: 'Fretes' },
  { value: 'acompanhamento_carga', label: 'Acompanhamento de Carga' },
  { value: 'motoristas', label: 'Motoristas' },
  { value: 'seguros', label: 'Seguros' },
  { value: 'fornecedores', label: 'Fornecedores' },
  { value: 'colaboradores', label: 'Colaboradores' },
  { value: 'perfis', label: 'Perfis' },
  { value: 'contas_pagar', label: 'Contas a Pagar' },
  { value: 'contas_receber', label: 'Contas a Receber' },
  { value: 'clientes', label: 'Clientes' },
  { value: 'naturezas', label: 'Naturezas' },
  { value: 'fornecedores_financeiro', label: 'Fornecedores (Financeiro)' },
  { value: 'fluxo_caixa', label: 'Fluxo de Caixa' },
];

const STEPS = [
  { id: 1, label: 'Dados do Perfil' },
  { id: 2, label: 'Módulos & Empresas' },
  { id: 3, label: 'Permissões de Acesso' },
];

function ProfileForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { profiles, currentProfile } = useSelector((state) => state.profiles);
  const { providers } = useSelector((state) => state.providers);
  const isEdit = !!id;
  const formRef = useRef(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [searchCompany, setSearchCompany] = useState('');
  const [activePermissionsModule, setActivePermissionsModule] = useState('transporte');

  const profile = isEdit ? currentProfile : null;

  useEffect(() => {
    if (!providers || providers.length === 0) {
      dispatch(ProvidersActions.getProvidersRequest());
    }

    if (isEdit && !currentProfile) {
      setLoadingProfile(true);
      dispatch(ProfilesActions.getProfileRequest(id));
    } else if (isEdit && currentProfile) {
      setLoadingProfile(false);
    }
  }, [dispatch, isEdit, id, currentProfile, providers]);

  // Obter lista de módulos disponíveis
  const availableModules = Object.values(MODULES).map(module => ({
    id: module.id,
    name: module.name,
  }));

  // Abas de permissões por módulo (organização visual)
  const PERMISSIONS_MODULE_TABS = [
    { id: 'transporte', label: 'Transportes' },
    { id: 'financeiro', label: 'Financeiro' },
    { id: 'embarcadores', label: 'Embarcador' },
  ];

  const REGISTRATION_TYPES_BY_MODULE = {
    transporte: [
      'cargas',
      'viagens',
      'pedidos_frete',
      'motoristas',
      'seguros',
      'fornecedores',
      'colaboradores',
      'perfis',
    ],
    financeiro: [
      'contas_pagar',
      'contas_receber',
      'clientes',
      'naturezas',
      'fornecedores_financeiro',
      'fluxo_caixa',
    ],
    embarcadores: [
      'embarcadores',
      'fretes',
      'acompanhamento_carga',
    ],
  };

  const getVisibleRegistrationTypes = () => {
    const allowed = REGISTRATION_TYPES_BY_MODULE[activePermissionsModule] || [];
    return REGISTRATION_TYPES.filter((r) => allowed.includes(r.value));
  };

  // Inicializar registrations com todos os tipos de cadastro
  const getInitialRegistrations = () => {
    if (profile?.registrations && profile.registrations.length > 0) {
      // Se já tem registrations do perfil, usar eles
      // Garantir que todos os tipos estejam presentes
      const registrationsMap = {};
      profile.registrations.forEach(reg => {
        registrationsMap[reg.registration_type] = reg;
      });

      // Adicionar tipos que não estão no perfil com valores padrão
      REGISTRATION_TYPES.forEach(regType => {
        if (!registrationsMap[regType.value]) {
          registrationsMap[regType.value] = {
            registration_type: regType.value,
            can_view: true,
            can_create: false,
            can_update: false,
            can_delete: false,
          };
        }
      });

      return Object.values(registrationsMap);
    } else {
      // Se não tem registrations, criar com valores padrão (apenas view marcado)
      return REGISTRATION_TYPES.map(regType => ({
        registration_type: regType.value,
        can_view: true,
        can_create: false,
        can_update: false,
        can_delete: false,
      }));
    }
  };

  const initialValues = {
    nome: profile?.nome || '',
    descricao: profile?.descricao || '',
    ativo: profile?.ativo !== undefined ? profile.ativo : true,
    modules: profile?.modules || [],
    companies: profile?.companies || [],
    registrations: getInitialRegistrations(),
  };

  const handleSubmit = (values) => {
    // Garantir que todos os tipos de cadastro estejam presentes nas registrations
    const registrationsMap = {};
    values.registrations.forEach(reg => {
      registrationsMap[reg.registration_type] = reg;
    });

    // Adicionar tipos que não estão presentes
    REGISTRATION_TYPES.forEach(regType => {
      if (!registrationsMap[regType.value]) {
        registrationsMap[regType.value] = {
          registration_type: regType.value,
          can_view: true,
          can_create: false,
          can_update: false,
          can_delete: false,
        };
      }
    });

    const dadosPerfil = {
      nome: values.nome.trim(), // Garantir que não há espaços extras
      descricao: values.descricao ? values.descricao.trim() : '',
      ativo: values.ativo,
      modules: values.modules || [],
      companies: values.companies || [],
      registrations: Object.values(registrationsMap).map(reg => ({
        registration_type: reg.registration_type,
        can_view: reg.can_view !== undefined ? reg.can_view : true,
        can_create: reg.can_create === true,
        can_update: reg.can_update === true,
        can_delete: reg.can_delete === true,
      })),
    };

    console.log('📤 [ProfileForm] Dados a serem enviados:', {
      ...dadosPerfil,
      registrationsCount: dadosPerfil.registrations.length,
    });

    if (isEdit) {
      console.log('📝 [ProfileForm] Atualizando perfil ID:', id);
      dispatch(ProfilesActions.updateProfileRequest(id, dadosPerfil, () => history.push('/profiles')));
    } else {
      console.log('➕ [ProfileForm] Criando novo perfil');
      dispatch(ProfilesActions.addProfileRequest(dadosPerfil, () => history.push('/profiles')));
    }
  };

  // Filtrar empresas por busca
  const filteredCompanies = providers?.filter(company => {
    const searchLower = searchCompany.toLowerCase();
    const name = (company.social_reason || company.nome_razao_social || company.razao_social || company.nome || '').toLowerCase();
    return name.includes(searchLower);
  }) || [];

  // Selecionar todas as permissões de um tipo (ou desmarcar todas)
  const handleSelectAllPermissions = (permissionType, values, setFieldValue, visibleRegTypes) => {
    const visibleSet = new Set((visibleRegTypes || []).map((r) => r.value));
    const visibleRegs = values.registrations.filter((r) => visibleSet.has(r.registration_type));

    // Se todas as visíveis estão marcadas, desmarcar; senão, marcar
    const allChecked = visibleRegs.length > 0 && visibleRegs.every((r) => r[permissionType] === true);
    const newValue = !allChecked;

    const newRegistrations = values.registrations.map((reg) => {
      if (!visibleSet.has(reg.registration_type)) return reg;
      return { ...reg, [permissionType]: newValue };
    });

    setFieldValue('registrations', newRegistrations);
  };

  if (isEdit && loadingProfile) {
    return <div>Carregando...</div>;
  }

  return (
    <Container>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{isEdit ? 'EDITAR PERFIL' : 'NOVO PERFIL'}</ModalTitle>
          <HeaderButtons>
            <HeaderSaveButton
              type="button"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.handleSubmit();
                }
              }}
            >
              SALVAR
            </HeaderSaveButton>
            <HeaderCancelButton
              type="button"
              onClick={() => history.push('/profiles')}
            >
              VOLTAR
            </HeaderCancelButton>
          </HeaderButtons>
        </ModalHeader>

        <StepsContainer>
          {STEPS.map((step) => (
            <Step
              key={step.id}
              active={activeStep === step.id}
              onClick={() => setActiveStep(step.id)}
            >
              <StepNumber active={activeStep === step.id}>
                {step.id}.
              </StepNumber>
              {step.label}
            </Step>
          ))}
        </StepsContainer>

        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, handleChange, setFieldValue, handleSubmit: formikHandleSubmit, errors, touched }) => (
            <form onSubmit={formikHandleSubmit}>
              {activeStep === 1 && (
                <FormContent>
                  <LeftPanel>
                    <Section>
                      <SectionTitle>Dados do Perfil</SectionTitle>
                      <WrapperInput>
                        <TextField
                          label="Nome do Perfil *"
                          name="nome"
                          value={values.nome}
                          onChange={handleChange}
                          error={touched.nome && Boolean(errors.nome)}
                          helperText={touched.nome && errors.nome}
                          fullWidth
                          color="secondary"
                        />
                      </WrapperInput>
                      <WrapperInput>
                        <TextField
                          label="Descrição"
                          name="descricao"
                          value={values.descricao}
                          onChange={handleChange}
                          multiline
                          rows={3}
                          fullWidth
                          color="secondary"
                        />
                      </WrapperInput>
                    </Section>
                  </LeftPanel>
                  <RightPanel>
                    <Section>
                      <SectionTitle>Status</SectionTitle>
                      <ToggleContainer>
                        <Switch
                          checked={values.ativo}
                          onChange={(e) => setFieldValue('ativo', e.target.checked)}
                          color="primary"
                        />
                        <ToggleLabel>Perfil Ativo</ToggleLabel>
                      </ToggleContainer>
                    </Section>
                  </RightPanel>
                </FormContent>
              )}

              {activeStep === 2 && (
                <FormContent>
                  <LeftPanel>
                    <Section>
                      <SectionTitle>Módulos</SectionTitle>
                      <ChipsContainer>
                        {availableModules.map((module) => (
                          <Chip
                            key={module.id}
                            type="button"
                            selected={values.modules.includes(module.id)}
                            onClick={() => {
                              if (values.modules.includes(module.id)) {
                                setFieldValue('modules', values.modules.filter(m => m !== module.id));
                              } else {
                                setFieldValue('modules', [...values.modules, module.id]);
                              }
                            }}
                          >
                            {module.name}
                          </Chip>
                        ))}
                      </ChipsContainer>
                    </Section>
                  </LeftPanel>
                  <RightPanel>
                    <Section>
                      <SectionTitle>Empresas</SectionTitle>
                      <CompaniesSection>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <TextField
                            placeholder="Buscar..."
                            color={"#666"}
                            value={searchCompany}
                            onChange={(e) => setSearchCompany(e.target.value)}
                            fullWidth
                          />
                          <SearchButton type="button">
                            <MdSearch size={20} />
                          </SearchButton>
                        </div>
                        {filteredCompanies.length > 0 ? (
                          filteredCompanies.map((company) => (
                            <CompanyCheckbox key={company.id}>
                              <Checkbox
                                checked={values.companies.includes(company.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFieldValue('companies', [...values.companies, company.id]);
                                  } else {
                                    setFieldValue('companies', values.companies.filter(c => c !== company.id));
                                  }
                                }}
                                color="primary"
                              />
                              <span style={{ fontSize: '14px' }}>
                                {company.social_reason || company.nome_razao_social || company.razao_social || company.nome || 'Sem nome'}
                              </span>
                            </CompanyCheckbox>
                          ))
                        ) : (
                          <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' }}>
                            {searchCompany ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa cadastrada'}
                          </p>
                        )}
                      </CompaniesSection>
                    </Section>
                  </RightPanel>
                </FormContent>
              )}

              {activeStep === 3 && (
                <PermissionsSection>
                  {(() => {
                    const visibleRegTypes = getVisibleRegistrationTypes();
                    const visibleSet = new Set(visibleRegTypes.map((r) => r.value));
                    const visibleRegs = values.registrations.filter((r) => visibleSet.has(r.registration_type));

                    const isAllChecked = (permissionType) =>
                      visibleRegs.length > 0 && visibleRegs.every((r) => r[permissionType] === true);

                    const isIndeterminate = (permissionType) =>
                      visibleRegs.length > 0 &&
                      visibleRegs.some((r) => r[permissionType] === true) &&
                      !isAllChecked(permissionType);

                    return (
                      <>
                        <PermissionsHeader>
                          <PermissionsTitle>3. Permissões de Acesso</PermissionsTitle>
                          <SelectAllContainer>
                            <span style={{ fontSize: '14px', marginRight: '8px' }}>Selecionar Todas</span>
                            <SearchIconContainer>
                              <MdSearch size={20} />
                            </SearchIconContainer>
                          </SelectAllContainer>
                        </PermissionsHeader>

                        {/* Abas por módulo */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', borderBottom: '1px solid #ddd' }}>
                          {PERMISSIONS_MODULE_TABS.map((tab) => (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() => setActivePermissionsModule(tab.id)}
                              style={{
                                padding: '10px 16px',
                                border: 'none',
                                borderRadius: '5px',
                                background: activePermissionsModule === tab.id ? '#122D58' : 'transparent',
                                color: activePermissionsModule === tab.id ? 'white' : '#122D58',
                                cursor: 'pointer',
                                borderBottom: activePermissionsModule === tab.id ? '2px solid #122D58' : 'none',
                              }}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        <PermissionsTableHeader>
                          <PermissionsTableCell>Cadastro</PermissionsTableCell>
                          <PermissionsTableCell style={{ justifyContent: 'center' }}>
                            <Checkbox
                              checked={isAllChecked('can_view')}
                              indeterminate={isIndeterminate('can_view')}
                              onChange={() => handleSelectAllPermissions('can_view', values, setFieldValue, visibleRegTypes)}
                              color="primary"
                            />
                            Visualizar
                          </PermissionsTableCell>
                          <PermissionsTableCell style={{ justifyContent: 'center' }}>
                            <Checkbox
                              checked={isAllChecked('can_create')}
                              indeterminate={isIndeterminate('can_create')}
                              onChange={() => handleSelectAllPermissions('can_create', values, setFieldValue, visibleRegTypes)}
                              color="primary"
                            />
                            Incluir
                          </PermissionsTableCell>
                          <PermissionsTableCell style={{ justifyContent: 'center' }}>
                            <Checkbox
                              checked={isAllChecked('can_update')}
                              indeterminate={isIndeterminate('can_update')}
                              onChange={() => handleSelectAllPermissions('can_update', values, setFieldValue, visibleRegTypes)}
                              color="primary"
                            />
                            Alterar
                          </PermissionsTableCell>
                          <PermissionsTableCell style={{ justifyContent: 'center' }}>
                            <Checkbox
                              checked={isAllChecked('can_delete')}
                              indeterminate={isIndeterminate('can_delete')}
                              onChange={() => handleSelectAllPermissions('can_delete', values, setFieldValue, visibleRegTypes)}
                              color="primary"
                            />
                            Excluir
                          </PermissionsTableCell>
                        </PermissionsTableHeader>

                        {visibleRegTypes.map((regType) => {
                          const existingReg = values.registrations.find((r) => r.registration_type === regType.value);
                          const regData = existingReg || {
                            registration_type: regType.value,
                            can_view: false,
                            can_create: false,
                            can_update: false,
                            can_delete: false,
                          };

                          return (
                            <PermissionsTableRow key={regType.value}>
                              <PermissionsTableCell style={{ fontWeight: '500' }}>
                                {regType.label}
                              </PermissionsTableCell>
                              <PermissionsTableCell style={{ justifyContent: 'center' }}>
                                <Checkbox
                                  checked={regData.can_view}
                                  onChange={(e) => {
                                    const newRegistrations = values.registrations.filter((r) => r.registration_type !== regType.value);
                                    newRegistrations.push({ ...regData, can_view: e.target.checked });
                                    setFieldValue('registrations', newRegistrations);
                                  }}
                                  color="primary"
                                />
                              </PermissionsTableCell>
                              <PermissionsTableCell style={{ justifyContent: 'center' }}>
                                <Checkbox
                                  checked={regData.can_create}
                                  onChange={(e) => {
                                    const newRegistrations = values.registrations.filter((r) => r.registration_type !== regType.value);
                                    newRegistrations.push({ ...regData, can_create: e.target.checked });
                                    setFieldValue('registrations', newRegistrations);
                                  }}
                                  color="primary"
                                />
                              </PermissionsTableCell>
                              <PermissionsTableCell style={{ justifyContent: 'center' }}>
                                <Checkbox
                                  checked={regData.can_update}
                                  onChange={(e) => {
                                    const newRegistrations = values.registrations.filter((r) => r.registration_type !== regType.value);
                                    newRegistrations.push({ ...regData, can_update: e.target.checked });
                                    setFieldValue('registrations', newRegistrations);
                                  }}
                                  color="primary"
                                />
                              </PermissionsTableCell>
                              <PermissionsTableCell style={{ justifyContent: 'center' }}>
                                <Checkbox
                                  checked={regData.can_delete}
                                  onChange={(e) => {
                                    const newRegistrations = values.registrations.filter((r) => r.registration_type !== regType.value);
                                    newRegistrations.push({ ...regData, can_delete: e.target.checked });
                                    setFieldValue('registrations', newRegistrations);
                                  }}
                                  color="primary"
                                />
                              </PermissionsTableCell>
                            </PermissionsTableRow>
                          );
                        })}
                      </>
                    );
                  })()}
                </PermissionsSection>
              )}

              <ModalFooter>
                <CancelButton type="button" onClick={() => history.push('/profiles')}>
                  CANCELAR
                </CancelButton>
                <SaveButton
                  type="button"
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.handleSubmit();
                    }
                  }}
                >
                  SALVAR
                </SaveButton>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </ModalContainer>
    </Container>
  );
}

export default ProfileForm;
